import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { ProtectedRoute } from "../components/ProtectedRoute";
import { fetchLeasesDirect, Lease } from "../api/dataverse";
import { dataverseScopes } from "../auth/msalConfig";

const LeaseSearchContent: React.FC = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [allLeases, setAllLeases] = useState<Lease[]>([]);
  const [leases, setLeases] = useState<Lease[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all leases once on mount — no search param sent to API
  useEffect(() => {
    setLoading(true);
    setError(null);
    instance
      .acquireTokenSilent({
        scopes: dataverseScopes,
        account: instance.getActiveAccount() || undefined,
      })
      .then((r) => fetchLeasesDirect(r.accessToken))
      .then((data) => { setAllLeases(data); setLeases(data); })
      .catch(() => setError("No se pudo cargar los leases."))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Client-side wildcard filter — runs instantly on every keystroke
  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) { setLeases(allLeases); return; }
    setLeases(allLeases.filter(l =>
      l.leaseId.toLowerCase().includes(q) ||
      l.clientName.toLowerCase().includes(q) ||
      l.numeroCliente.toLowerCase().includes(q) ||
      l.parcel.toLowerCase().includes(q) ||
      l.contract.toLowerCase().includes(q)
    ));
  }, [search, allLeases]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault(); // search effect already running on every keystroke
  };

  const fmtCurrency = (n: number) =>
    n > 0
      ? n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 })
      : "—";

  const statusBadge = (status: string) => {
    const cls =
      status === "Activo"     ? "bg-green-100 text-green-700"  :
      status === "Terminado"  ? "bg-gray-100 text-gray-500"    :
      status === "Cancelado"  ? "bg-red-100 text-red-700"      :
                                "bg-blue-100 text-blue-700";
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{status}</span>;
  };

  // Summary card counts based on actual leasestatus data
  const activeCount      = leases.filter(l => l.status === "Activo").length;
  const terminatedCount  = leases.filter(l => l.status === "Terminado").length;
  const noContractCount  = leases.filter(l => l.contract === "—").length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Leases</h1>
        <p className="text-gray-500 mt-1">Contratos de arrendamiento activos e históricos</p>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="flex gap-3 mb-6">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por número de lease o cliente..."
          className="border border-gray-300 rounded-md px-3 py-2 text-sm flex-1 max-w-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition"
        >
          Buscar
        </button>
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="px-4 py-2 bg-gray-100 text-gray-600 text-sm rounded-md hover:bg-gray-200 transition"
          >
            Limpiar
          </button>
        )}
      </form>

      {/* Summary cards — based on leasestatus choice field */}
      {!loading && !error && leases.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{leases.length}</div>
            <div className="text-xs text-gray-500">Total Leases</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{activeCount}</div>
            <div className="text-xs text-gray-500">Activos</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-gray-500">{terminatedCount}</div>
            <div className="text-xs text-gray-500">Terminados</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-amber-600">{noContractCount}</div>
            <div className="text-xs text-gray-500">Sin Contrato</div>
          </div>
        </div>
      )}

      {loading && <p className="text-gray-500 text-sm">Cargando leases...</p>}
      {error   && <p className="text-red-600 text-sm">{error}</p>}

      {!loading && !error && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <span className="text-sm font-medium text-gray-700">{leases.length} leases encontrados</span>
          </div>

          {leases.length === 0 ? (
            <div className="p-12 text-center text-gray-400">No se encontraron leases.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {["Lease ID","Cliente","Sub-Parcela","Contrato","Valor en Libros","Estado"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leases.map((l) => (
                    <tr
                      key={l.id}
                      onClick={() => navigate(`/contratos/${l.contract}`)}
                      className="hover:bg-blue-50 cursor-pointer transition"
                    >
                      <td className="px-4 py-3 font-semibold text-blue-600">{l.leaseId}</td>
                      <td className="px-4 py-3 text-gray-900">
                        <div>{l.clientName}</div>
                        <div className="text-xs text-gray-400">{l.numeroCliente}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{l.parcel}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{l.contract}</td>
                      <td className="px-4 py-3 text-gray-700">{fmtCurrency(l.valorEnLibro)}</td>
                      <td className="px-4 py-3">{statusBadge(l.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const LeaseSearchPage: React.FC = () => (
  <ProtectedRoute>
    <LeaseSearchContent />
  </ProtectedRoute>
);
